"use client";

import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  Star,
  Quote,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  CheckCircle2,
  SlidersHorizontal,
  LayoutGrid,
  Calendar,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchServiceReviews } from "@/store/slices/landingSlice";
import { imageUrl } from "@/services/BaseUrl";
import type { ServiceReview } from "@/types/landing";

interface ServiceReviewsSectionProps {
  embedded?: boolean;
}

const ServiceReviewsSection: React.FC<ServiceReviewsSectionProps> = ({
  embedded = false,
}) => {
  const dispatch = useAppDispatch();
  const { data: serviceReviews, loading } = useAppSelector(
    (state) => state.landing.serviceReviews,
  );
  const { mode } = useAppSelector((state) => state.theme);
  const isDark = mode === "dark";

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [cardsPerPage, setCardsPerPage] = useState(3);
  const [viewMode, setViewMode] = useState<"carousel" | "grid">("carousel");

  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const dragStartXRef = useRef<number | null>(null);

  useEffect(() => {
    dispatch(fetchServiceReviews());
  }, [dispatch]);

  // Handle responsive cards per page
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setCardsPerPage(1);
      } else if (window.innerWidth < 1024) {
        setCardsPerPage(2);
      } else {
        setCardsPerPage(3);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const totalReviews = serviceReviews?.length || 0;
  const maxIndex = Math.max(0, totalReviews - cardsPerPage);

  // Auto-play timer
  useEffect(() => {
    if (isPaused || maxIndex <= 0 || viewMode !== "carousel") return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
    }, 4500);
    return () => clearInterval(interval);
  }, [isPaused, maxIndex, viewMode]);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  // Touch Swipe handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > 50) handleNext();
    else if (distance < -50) handlePrev();
  };

  // Mouse drag handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    dragStartXRef.current = e.clientX;
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    if (dragStartXRef.current === null) return;
    const diff = dragStartXRef.current - e.clientX;
    if (diff > 50) handleNext();
    else if (diff < -50) handlePrev();
    dragStartXRef.current = null;
  };

  if (!loading && (!serviceReviews || serviceReviews.length === 0)) {
    return null; // Gracefully hide if no service reviews exist yet
  }

  // Calculate average rating
  const avgRating =
    totalReviews > 0
      ? (
          serviceReviews.reduce((acc, curr) => acc + (curr.rating || 5), 0) /
          totalReviews
        ).toFixed(1)
      : "5.0";

  const content = (
    <div
      className={
        embedded
          ? "w-full"
          : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10"
      }
    >
      {/* ── Section Header ── */}
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 sm:mb-16 gap-6">
        <div className="max-w-2xl text-left">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4 border transition-all ${
              isDark
                ? "bg-[#8B31FF]/10 text-[#8B31FF] border-[#8B31FF]/30"
                : "bg-purple-50 text-[#8B31FF] border-purple-200"
            }`}
          >
            <Sparkles className="w-3.5 h-3.5" />
            Verified Service Reviews
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className={`text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight mb-4 ${
              isDark ? "text-white" : "text-gray-900"
            }`}
          >
            Client Feedback on Our{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#8B31FF] via-purple-500 to-[#6d1ec9]">
              Expert Services
            </span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className={`text-base sm:text-lg leading-relaxed ${
              isDark ? "text-gray-400" : "text-gray-600"
            }`}
          >
            Discover how our engineering, design, and digital solutions have
            accelerated growth for founders, businesses, and enterprise teams
            worldwide.
          </motion.p>
        </div>

        {/* Rating Snapshot & Controls */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 flex-shrink-0">
          {/* Aggregate Score Pill */}
          <div
            className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border backdrop-blur-md shadow-sm ${
              isDark
                ? "bg-gray-900/80 border-gray-800 text-white"
                : "bg-white border-gray-200 text-gray-900"
            }`}
          >
            <div className="flex text-amber-400">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={16} fill="currentColor" />
              ))}
            </div>
            <div className="text-sm font-bold flex items-center gap-1.5">
              <span>{avgRating}</span>
              <span
                className={`text-xs font-normal ${isDark ? "text-gray-400" : "text-gray-500"}`}
              >
                ({totalReviews} Reviews)
              </span>
            </div>
          </div>

          {/* View Mode & Carousel Navigation */}
          <div className="flex items-center gap-2">
            <button
              onClick={() =>
                setViewMode(viewMode === "carousel" ? "grid" : "carousel")
              }
              className={`p-2.5 rounded-lg border transition-all cursor-pointer ${
                isDark
                  ? "bg-gray-800 hover:bg-gray-700 border-gray-700 text-gray-300"
                  : "bg-white hover:bg-gray-50 border-gray-200 text-gray-700 shadow-sm"
              }`}
              title={
                viewMode === "carousel"
                  ? "Switch to Grid View"
                  : "Switch to Carousel View"
              }
            >
              {viewMode === "carousel" ? (
                <LayoutGrid size={18} />
              ) : (
                <SlidersHorizontal size={18} />
              )}
            </button>

            {viewMode === "carousel" && (
              <>
                <button
                  onClick={handlePrev}
                  className={`p-2.5 rounded-lg border transition-all cursor-pointer ${
                    isDark
                      ? "bg-gray-800 hover:bg-gray-700 border-gray-700 text-gray-200 hover:text-white"
                      : "bg-white hover:bg-gray-50 border-gray-200 text-gray-700 shadow-sm"
                  }`}
                  aria-label="Previous service review"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  onClick={handleNext}
                  className={`p-2.5 rounded-lg border transition-all cursor-pointer ${
                    isDark
                      ? "bg-gray-800 hover:bg-gray-700 border-gray-700 text-gray-200 hover:text-white"
                      : "bg-white hover:bg-gray-50 border-gray-200 text-gray-700 shadow-sm"
                  }`}
                  aria-label="Next service review"
                >
                  <ChevronRight size={18} />
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Carousel View ── */}
      {viewMode === "carousel" ? (
        <div
          className="relative overflow-hidden cursor-grab active:cursor-grabbing select-none text-left pt-6 pb-8 -mt-2 -mb-4 px-1"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
        >
          <motion.div
            className="flex gap-6 py-2"
            animate={{
              x: `-${currentIndex * (100 / cardsPerPage)}%`,
            }}
            transition={{ type: "spring", stiffness: 260, damping: 28 }}
          >
            {serviceReviews.map((item: ServiceReview, index: number) => {
              const reviewId = item._id || item.id || index;
              const hasImage =
                item.image &&
                (item.image.startsWith("http") ||
                  item.image.startsWith("/upload") ||
                  item.image.startsWith("upload"));

              return (
                <div
                  key={reviewId}
                  className="flex-shrink-0"
                  style={{
                    width: `calc(${100 / cardsPerPage}% - ${
                      (24 * (cardsPerPage - 1)) / cardsPerPage
                    }px)`,
                  }}
                >
                  <div
                    className={`group relative h-full rounded-3xl border p-7 sm:p-8 flex flex-col justify-between transition-all duration-300 overflow-hidden ${
                      isDark
                        ? "bg-gray-900/80 border-gray-800 hover:border-[#8B31FF]/60 hover:bg-gray-800/90 shadow-lg hover:shadow-2xl hover:shadow-[#8B31FF]/10"
                        : "bg-white border-gray-200/90 hover:border-purple-300 hover:bg-white shadow-sm hover:shadow-xl hover:shadow-purple-500/10"
                    } hover:-translate-y-1.5`}
                  >
                    {/* Top Accent Gradient Bar on hover */}
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#8B31FF] to-[#6d1ec9] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                    <div>
                      {/* Rating Row & Quote Icon */}
                      <div className="flex items-center justify-between mb-5">
                        <div className="flex items-center gap-1 text-amber-400">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              size={16}
                              fill={
                                i < (item.rating || 5) ? "currentColor" : "none"
                              }
                              strokeWidth={i < (item.rating || 5) ? 0 : 1.5}
                              className={
                                i < (item.rating || 5)
                                  ? ""
                                  : "text-gray-300 dark:text-gray-600"
                              }
                            />
                          ))}
                        </div>
                        <div
                          className={`p-2 rounded-lg transition-colors ${
                            isDark
                              ? "bg-gray-800 text-[#8B31FF] group-hover:bg-[#8B31FF]/20"
                              : "bg-purple-50 text-[#8B31FF] group-hover:bg-purple-100"
                          }`}
                        >
                          <Quote size={18} className="transform rotate-180" />
                        </div>
                      </div>

                      {/* Review Body Text */}
                      <p
                        className={`text-sm sm:text-base leading-relaxed mb-6 font-medium ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        &quot;{item.text}&quot;
                      </p>
                    </div>

                    {/* Author Details Footer */}
                    <div className="pt-5 border-t border-gray-100 dark:border-gray-800/80 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3.5">
                        <div
                          className={`w-11 h-11 rounded-full overflow-hidden flex-shrink-0 border ${
                            isDark
                              ? "bg-gray-800 border-gray-700"
                              : "bg-gray-100 border-gray-200"
                          }`}
                        >
                          {hasImage ? (
                            <img
                              src={
                                item.image.startsWith("http")
                                  ? item.image
                                  : `${imageUrl}${item.image.startsWith("/") ? "" : "/"}${item.image}`
                              }
                              alt={item.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-[#8B31FF] to-[#6d1ec9] flex items-center justify-center text-white font-extrabold text-sm">
                              {item.name
                                ? item.name.charAt(0).toUpperCase()
                                : "C"}
                            </div>
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-1.5">
                            <h4
                              className={`text-sm font-bold ${isDark ? "text-white" : "text-gray-900"}`}
                            >
                              {item.name}
                            </h4>
                            <CheckCircle2
                              size={13}
                              className="text-[#8B31FF]"
                            />
                          </div>
                          <p
                            className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}
                          >
                            {item.role}
                          </p>
                        </div>
                      </div>

                      {item.createdAt && (
                        <div
                          className={`hidden sm:flex items-center gap-1 text-[11px] font-medium ${
                            isDark ? "text-gray-500" : "text-gray-400"
                          }`}
                        >
                          <Calendar size={12} />
                          {new Date(item.createdAt).toLocaleDateString(
                            undefined,
                            {
                              month: "short",
                              year: "numeric",
                            },
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </motion.div>
        </div>
      ) : (
        /* ── Grid View ── */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 text-left">
          {serviceReviews.map((item: ServiceReview, index: number) => {
            const reviewId = item._id || item.id || index;
            const hasImage =
              item.image &&
              (item.image.startsWith("http") ||
                item.image.startsWith("/upload") ||
                item.image.startsWith("upload"));

            return (
              <motion.div
                key={reviewId}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className={`group relative rounded-3xl border p-7 sm:p-8 flex flex-col justify-between transition-all duration-300 overflow-hidden ${
                  isDark
                    ? "bg-gray-900/80 border-gray-800 hover:border-[#8B31FF]/60 hover:bg-gray-800/90 shadow-lg hover:shadow-2xl hover:shadow-[#8B31FF]/10"
                    : "bg-white border-gray-200/90 hover:border-purple-300 hover:bg-white shadow-sm hover:shadow-xl hover:shadow-purple-500/10"
                } hover:-translate-y-1.5`}
              >
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#8B31FF] to-[#6d1ec9] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                <div>
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-1 text-amber-400">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          size={16}
                          fill={
                            i < (item.rating || 5) ? "currentColor" : "none"
                          }
                          strokeWidth={i < (item.rating || 5) ? 0 : 1.5}
                          className={
                            i < (item.rating || 5)
                              ? ""
                              : "text-gray-300 dark:text-gray-600"
                          }
                        />
                      ))}
                    </div>
                    <div
                      className={`p-2 rounded-lg transition-colors ${
                        isDark
                          ? "bg-gray-800 text-[#8B31FF] group-hover:bg-[#8B31FF]/20"
                          : "bg-purple-50 text-[#8B31FF] group-hover:bg-purple-100"
                      }`}
                    >
                      <Quote size={18} className="transform rotate-180" />
                    </div>
                  </div>

                  <p
                    className={`text-sm sm:text-base leading-relaxed mb-6 font-medium ${
                      isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    &quot;{item.text}&quot;
                  </p>
                </div>

                <div className="pt-5 border-t border-gray-100 dark:border-gray-800/80 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3.5">
                    <div
                      className={`w-11 h-11 rounded-full overflow-hidden flex-shrink-0 border ${
                        isDark
                          ? "bg-gray-800 border-gray-700"
                          : "bg-gray-100 border-gray-200"
                      }`}
                    >
                      {hasImage ? (
                        <img
                          src={
                            item.image.startsWith("http")
                              ? item.image
                              : `${imageUrl}${item.image.startsWith("/") ? "" : "/"}${item.image}`
                          }
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#8B31FF] to-[#6d1ec9] flex items-center justify-center text-white font-extrabold text-sm">
                          {item.name ? item.name.charAt(0).toUpperCase() : "C"}
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5">
                        <h4
                          className={`text-sm font-bold ${isDark ? "text-white" : "text-gray-900"}`}
                        >
                          {item.name}
                        </h4>
                        <CheckCircle2 size={13} className="text-[#8B31FF]" />
                      </div>
                      <p
                        className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}
                      >
                        {item.role}
                      </p>
                    </div>
                  </div>

                  {item.createdAt && (
                    <div
                      className={`hidden sm:flex items-center gap-1 text-[11px] font-medium ${
                        isDark ? "text-gray-500" : "text-gray-400"
                      }`}
                    >
                      <Calendar size={12} />
                      {new Date(item.createdAt).toLocaleDateString(undefined, {
                        month: "short",
                        year: "numeric",
                      })}
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* ── Carousel Pagination Dots ── */}
      {viewMode === "carousel" && maxIndex > 0 && (
        <div className="flex items-center justify-center gap-2 mt-8">
          {Array.from({ length: maxIndex + 1 }).map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              aria-label={`Go to slide ${idx + 1}`}
              className={`transition-all duration-300 rounded-full cursor-pointer ${
                currentIndex === idx
                  ? "w-8 h-2.5 bg-gradient-to-r from-[#8B31FF] to-[#6d1ec9]"
                  : `w-2.5 h-2.5 ${isDark ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-300 hover:bg-gray-400"}`
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );

  if (embedded) {
    return <div className="w-full mb-20">{content}</div>;
  }

  return (
    <section
      className={`py-24 sm:py-32 relative overflow-hidden transition-colors duration-700 ${
        isDark ? "bg-[#0B0F19]" : "bg-slate-50"
      }`}
    >
      {/* ── Background Grid & Glow Blobs ── */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className={`absolute inset-0 transition-opacity duration-700 ${
            isDark ? "opacity-[0.05]" : "opacity-[0.03]"
          }`}
          style={{
            backgroundImage: isDark
              ? `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`
              : `linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />
        <div
          className={`absolute top-1/4 -left-32 w-96 h-96 rounded-full blur-[140px] pointer-events-none transition-opacity duration-700 ${
            isDark ? "bg-[#8B31FF]/15" : "bg-[#8B31FF]/8"
          }`}
        />
        <div
          className={`absolute bottom-1/4 -right-32 w-96 h-96 rounded-full blur-[140px] pointer-events-none transition-opacity duration-700 ${
            isDark ? "bg-[#6d1ec9]/15" : "bg-[#6d1ec9]/8"
          }`}
        />
      </div>

      {content}
    </section>
  );
};

export default ServiceReviewsSection;
